import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { formatCurrency, formatPercent, formatRatio } from "~/lib/format";
import type { FinancialRatios, YearSummary } from "~/types/financials";
import { ChangeIndicator } from "./change-indicator";

interface RatioCardsProps {
	years: YearSummary[];
}

interface RatioGroup {
	key: string;
	label: string;
	ratios: {
		label: string;
		key: keyof FinancialRatios;
		format: (v: number | null) => string;
	}[];
}

const RATIO_GROUPS: RatioGroup[] = [
	{
		key: "likvidita",
		label: "Likvidita",
		ratios: [
			{ label: "Běžná likvidita", key: "currentRatio", format: formatRatio },
			{ label: "Pohotová likvidita", key: "quickRatio", format: formatRatio },
			{ label: "Cash flow likvidita", key: "cashFlowLiquidity", format: formatRatio },
		],
	},
	{
		key: "ziskovost",
		label: "Ziskovost",
		ratios: [
			{ label: "ROE", key: "returnOnEquity", format: formatPercent },
			{ label: "ROA", key: "returnOnAssets", format: formatPercent },
			{ label: "Zisková marže", key: "profitMargin", format: formatPercent },
			{ label: "EBITDA marže", key: "ebitdaMargin", format: formatPercent },
			{ label: "Provozní marže", key: "ebitMargin", format: formatPercent },
			{ label: "Hrubá marže", key: "grossMargin", format: formatPercent },
			{ label: "ROCE", key: "roce", format: formatPercent },
		],
	},
	{
		key: "zadluzenost",
		label: "Zadluženost",
		ratios: [
			{ label: "Dluh / VK", key: "debtToEquity", format: formatRatio },
			{ label: "Míra zadluženosti", key: "debtRatio", format: formatPercent },
			{ label: "Míra samofinancování", key: "equityRatio", format: formatPercent },
			{ label: "Čistý pracovní kapitál", key: "netWorkingCapital", format: formatCurrency },
		],
	},
	{
		key: "efektivita",
		label: "Efektivita",
		ratios: [
			{ label: "Obrat aktiv", key: "assetTurnover", format: formatRatio },
			{ label: "Obrat stálých aktiv", key: "fixedAssetTurnover", format: formatRatio },
			{ label: "Obrat oběžných aktiv", key: "currentAssetTurnover", format: formatRatio },
			{ label: "Tržby / zaměstnance", key: "revenuePerEmployee", format: formatCurrency },
		],
	},
	{
		key: "cash-flow",
		label: "Cash Flow",
		ratios: [
			{ label: "CF marže", key: "cashFlowMargin", format: formatPercent },
			{ label: "CF výnosnost aktiv", key: "cashFlowRoa", format: formatPercent },
			{ label: "Dluh / CF", key: "debtToCashFlow", format: formatRatio },
			{ label: "Samofinancování investic", key: "cashFlowSelfFinancing", format: formatRatio },
		],
	},
	{
		key: "altman",
		label: "Altman Z-Score",
		ratios: [
			{ label: "Z-Score", key: "altmanZScore", format: formatRatio },
			{ label: "X1 (prac. kapitál/aktiva)", key: "altmanX1", format: formatRatio },
			{ label: "X3 (EBIT/aktiva)", key: "altmanX3", format: formatRatio },
			{ label: "X4 (VK/dluhy)", key: "altmanX4", format: formatRatio },
		],
	},
];

function ratioChange(
	current: FinancialRatios,
	prior: FinancialRatios | null,
	key: keyof FinancialRatios,
): number | null {
	if (!prior) return null;
	const curr = current[key];
	const prev = prior[key];
	if (curr == null || prev == null || prev === 0) return null;
	return (curr - prev) / Math.abs(prev);
}

function AltmanBadge({ zScore }: { zScore: number | null }) {
	if (zScore == null) return null;

	let label: string;
	let className: string;
	if (zScore > 2.9) {
		label = "Bezpečná zóna";
		className = "bg-success/15 text-success";
	} else if (zScore >= 1.23) {
		label = "Šedá zóna";
		className = "bg-warning/15 text-warning";
	} else {
		label = "Tísňová zóna";
		className = "bg-error/15 text-error";
	}

	return (
		<span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${className}`}>
			{label}
		</span>
	);
}

export function RatioCards({ years }: RatioCardsProps) {
	const latest = years[years.length - 1];
	const prior = years.length > 1 ? years[years.length - 2] : null;

	if (!latest) return null;
	const visibleGroups = RATIO_GROUPS.map((group) => ({
		...group,
		ratios:
			group.key === "altman" && latest.ratios.altmanZScore == null
				? []
				: group.ratios.filter((ratio) => latest.ratios[ratio.key] != null),
	})).filter((group) => group.ratios.length > 0);
	if (visibleGroups.length === 0) return null;

	return (
		<div className="card-surface overflow-hidden rounded-2xl">
			<div className="px-6 pt-6">
				<p className="label-overline">Finanční analýza</p>
				<h3 className="mt-1 text-lg font-semibold">Ekonomické ukazatele</h3>
				<p className="mt-1 text-xs text-muted">
					Hodnoty za rok {latest.year}
					{prior ? ` a změna oproti roku ${prior.year}` : ""}
				</p>
			</div>

			<Tabs defaultValue={visibleGroups[0].key} className="mt-4 gap-0">
				<TabsList aria-label="Skupiny ekonomických ukazatelů" className="px-2 sm:px-4">
					{visibleGroups.map((group) => (
						<TabsTrigger key={group.key} value={group.key}>
							{group.label}
						</TabsTrigger>
					))}
				</TabsList>

				{visibleGroups.map((group) => (
					<TabsContent key={group.key} value={group.key} className="mt-0">
						<div className="flex items-center justify-between gap-3 bg-gray-50 px-6 py-3">
							<p className="text-sm font-semibold">{group.label}</p>
							{group.key === "altman" && <AltmanBadge zScore={latest.ratios.altmanZScore} />}
						</div>
						<div className="overflow-x-auto">
							<table className="w-full min-w-[32rem] text-sm">
								<caption className="sr-only">
									{group.label}: hodnoty za rok {latest.year} a meziroční změny
								</caption>
								<thead>
									<tr className="border-b border-gray-200 text-xs text-muted">
										<th className="px-6 py-2.5 text-left font-medium">Ukazatel</th>
										<th className="px-4 py-2.5 text-right font-medium">{latest.year}</th>
										<th className="px-6 py-2.5 text-right font-medium">Meziroční změna</th>
									</tr>
								</thead>
								<tbody>
									{group.ratios.map((ratio) => (
										<tr key={ratio.key} className="border-b border-gray-100 last:border-b-0">
											<td className="px-6 py-3 text-muted">{ratio.label}</td>
											<td className="px-4 py-3 text-right font-mono font-medium tabular-nums">
												{ratio.format(latest.ratios[ratio.key])}
											</td>
											<td className="px-6 py-3 text-right">
												<ChangeIndicator
													value={ratioChange(latest.ratios, prior?.ratios ?? null, ratio.key)}
												/>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</TabsContent>
				))}
			</Tabs>
		</div>
	);
}
